import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FebStatut, IApplication, IFicheExpressionBesoinResponse } from 'src/app/shared/models/fiche-expression-besoin';
import { FicheExpressionBesoinService } from 'src/app/core/fiche-expression-besoin/fiche-expression-besoin.service';
import { DevisService } from '../devis.service';
import { IDevisCreateRequest } from 'src/app/shared/models/devis';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { FebActiviteUoNomenclatureService } from 'src/app/core/feb-activite-uo-nomenclature-management/feb-activite-uo-nomenclature.service';
import { IFebActiviteUoNomenclature } from 'src/app/shared/models/feb-activite-uo-nomenclature';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';

@Component({
  selector: 'app-devis-create-dialog',
  templateUrl: './devis-create-dialog.component.html',
  styleUrls: ['./devis-create-dialog.component.less']
})
export class DevisCreateDialogComponent implements OnInit {

  searchCtrl = new FormControl('', Validators.required);
  devisForm: FormGroup;
  selectedFeb: IFicheExpressionBesoinResponse | null = null;
  selectedNomenclature: IFebActiviteUoNomenclature | null = null;

  febs: IFicheExpressionBesoinResponse[] = [];
  nomenclatures: IFebActiviteUoNomenclature[] = [];
  filteredFebs$!: Observable<IFicheExpressionBesoinResponse[]>;
  loading  = false;
  creating = false;

  constructor(
    private fb: FormBuilder,
    private febService: FicheExpressionBesoinService,
    private authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService,
    private devisService: DevisService,
    private nomenclatureService: FebActiviteUoNomenclatureService,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<DevisCreateDialogComponent>
  ) {
    this.devisForm = this.fb.group({
      devisMajore:          [false],
      devisDateDemarrageGo: [null],
      devisDuree:           [null],
      devisProvisionFrais:  [0],   // 0 par défaut, prérempli depuis FEB si disponible
      devisPrixUoPropose:   [null],
      devisBeneficiaire:    [{ value: null, disabled: true }],
      devisNumeroContrat:   [null],
      devisNomContrat:      [null],
      devisCommentaire:     [null],
      // ── Date d'entretien — obligatoire ────────────────────────
      devisDateEntretien:   [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.devisService.getNumContrat().subscribe((numContrat: string) => {
      this.devisForm.get('devisNumeroContrat')?.setValue(numContrat, { emitEvent: false });
      this.devisForm.get('devisNumeroContrat')?.disable();
    });

    this.devisService.getNomContrat().subscribe((nomContrat: string) => {
      this.devisForm.get('devisNomContrat')?.setValue(nomContrat, { emitEvent: false });
      this.devisForm.get('devisNomContrat')?.disable();
    });

    this.devisForm.get('devisMajore')!.valueChanges.subscribe((majore: boolean) => {
      if (this.selectedNomenclature) {
        const prix = majore
          ? this.selectedNomenclature.prixUOmajore
          : this.selectedNomenclature.prixUO;
        this.devisForm.get('devisPrixUoPropose')!.setValue(prix ?? null, { emitEvent: false });
      }
    });

    const apps: IApplication[] = this.currentUserService.getUserApplications();

    this.loading = true;
    forkJoin({
      febs: this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS()
        ? this.febService.getAll()
        : this.febService.getByApplications(apps),
      devisList: this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS()
        ? this.devisService.getAll()
        : this.devisService.getByApplications(apps),
      nomenclatures: this.nomenclatureService.getAll()
    }).subscribe({
      next: ({ febs, devisList, nomenclatures }) => {
        this.nomenclatures = nomenclatures;
        const febIdsWithDevis = new Set(devisList.map(d => d.febId));
        this.febs = febs.filter(f =>
          f.febStatut === FebStatut.EN_ATTENTE &&
          !febIdsWithDevis.has(f.febId)
        );
        this.loading = false;
        this.filteredFebs$ = this.searchCtrl.valueChanges.pipe(
          startWith(''),
          map(val => this.filterFebs(val ?? ''))
        );
      },
      error: () => {
        this.snackbarService.openSnackBar('Erreur lors du chargement des FEB', 'error-snack-bar');
        this.loading = false;
      }
    });
  }

  private filterFebs(value: string | IFicheExpressionBesoinResponse): IFicheExpressionBesoinResponse[] {
    const term = typeof value === 'string'
      ? value.toLowerCase()
      : (value?.febLabel ?? '').toLowerCase();
    return this.febs.filter(f =>
      f.febLabel?.toLowerCase().includes(term) ||
      f.febDemandeurNom?.toLowerCase().includes(term)
    );
  }

  displayFn(feb: IFicheExpressionBesoinResponse | string | null): string {
    if (!feb || typeof feb === 'string') return feb as string ?? '';
    return feb.febLabel + (feb.febDemandeurNom ? ' — ' + feb.febDemandeurNom : '');
  }

  onSelectFeb(feb: IFicheExpressionBesoinResponse): void {
    this.selectedFeb = feb;
    this.selectedNomenclature = this.nomenclatures.find(
      n => (n.nomenclatureId ?? n.id) === feb.febUoActivitePressentiId
    ) ?? null;
    const majore = this.devisForm.get('devisMajore')!.value;
    const prixPropose = majore
      ? (this.selectedNomenclature?.prixUOmajore ?? null)
      : (this.selectedNomenclature?.prixUO ?? null);
    this.devisForm.patchValue({
      devisDateDemarrageGo: feb.febDateDemarrage ? new Date(feb.febDateDemarrage) : null,
      devisDuree:           feb.febDuree ?? null,
      devisProvisionFrais:  feb.febProvisionDeplacementAstreinte != null
                              ? Math.round(feb.febProvisionDeplacementAstreinte * 100) / 100
                              : 0,
      devisPrixUoPropose:   prixPropose,
      devisBeneficiaire:    feb.febDemandeurNom ?? null
    });
  }

  get prixUoGrilleDisplay(): number | null {
    if (!this.selectedNomenclature) return null;
    const majore = this.devisForm.get('devisMajore')?.value;
    return majore
      ? (this.selectedNomenclature.prixUOmajore ?? null)
      : (this.selectedNomenclature.prixUO ?? null);
  }

  get montantTotalCalcule(): number | null {
    const prix      = this.devisForm.get('devisPrixUoPropose')?.value;
    const duree     = this.devisForm.get('devisDuree')?.value;
    const provision = this.devisForm.get('devisProvisionFrais')?.value;
    if (prix == null || duree == null) return null;
    return Math.round((+prix * +duree + +(provision ?? 0)) * 100) / 100;
  }

  canCreate(): boolean {
    return this.selectedFeb != null && this.devisForm.get('devisDateEntretien')!.valid;
  }

  private toLocalDateStr(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  onCreate(): void {
    if (!this.selectedFeb || this.creating) return;

    // Marque le champ date entretien comme touché pour afficher l'erreur si vide
    this.devisForm.get('devisDateEntretien')!.markAsTouched();
    if (this.devisForm.get('devisDateEntretien')!.invalid) return;

    this.creating = true;
    const raw = this.devisForm.getRawValue();

    const request: IDevisCreateRequest = {
      ...raw,
      febId: this.selectedFeb.febId,
      devisDateDemarrageGo: raw.devisDateDemarrageGo instanceof Date
        ? this.toLocalDateStr(raw.devisDateDemarrageGo)
        : raw.devisDateDemarrageGo,
      dateEntretien: raw.devisDateEntretien instanceof Date
        ? this.toLocalDateStr(raw.devisDateEntretien)
        : raw.devisDateEntretien
    };

    this.devisService.create(request).subscribe({
      next: devis => {
        this.snackbarService.openSnackBar('Devis créé avec succès');
        this.dialogRef.close(devis);
      },
      error: () => {
        this.snackbarService.openSnackBar('Erreur lors de la création du devis', 'error-snack-bar');
        this.creating = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}